import * as Yup from "yup";
import React from "react";
import PropTypes from "prop-types";
import {
  NAME_REGEX,
  NUMBER_REGEX,
  SPECIAL_CHARACTERS_REGEX,
  SPECIAL_CHARACTERS_NEWLINE_REGEX
} from "../../../../../utils/validation";
import {
  getInvalidMessage,
  getRequiredMessage,
  getMinDateMessage
} from "../../../../../utils/getValidationMessage";
import {
  MAX_ADDRESS_FIELD_LENGTH,
  Max_MAKANI_NUMBER_LENGTH
} from "../../../../CompanyInfo/constants";
// Removed unused imports
import {
  OTHER_CONTACT_DESIGNATION,
  MAX_COMPANY_BACKGROUND_LENGTH,
  COMPANY_BACKGROUND
} from "./constants";
import { format, isValid } from "date-fns";
import { UAE_CODE, DATE_FORMAT, DATE_TIME_FORMAT } from "../../../../../constants";

export const companyDetailsSchema = () =>
  Yup.object().shape({
    visitValidations: Yup.array().of(
      Yup.object().shape({
        contactSalutation: Yup.string(),
        contactfullName: Yup.string()
          .nullable()
          .max(80, "Max character length is 80")
          .matches(NAME_REGEX, getInvalidMessage("Name")),
        countryCode: Yup.string(),
        mobileNo: Yup.string()
          .nullable()
          .phoneNo({
            codeFieldName: "countryCode",
            fieldName: "Mobile Number"
          }),
        contactDesignation: Yup.string()
          .nullable()
          .max(50, "Max character length is 50"),
        contactDesignationOther: Yup.string()
          .nullable()
          .when("contactDesignation", {
            is: contactDesignation => contactDesignation === OTHER_CONTACT_DESIGNATION,
            then: Yup.string() // NOSONAR
              .required(getRequiredMessage("Other Designation"))
              .matches(NAME_REGEX, getInvalidMessage("Other Designation"))
          }),
        roName: Yup.string().nullable(),
        roDateOfVisit: Yup.date()
          .nullable()
          .min(new Date(1900, 0, 1), getMinDateMessage("Date of Visit"))
          .max(new Date(), getInvalidMessage("Date of Visit"))
          .typeError(getInvalidMessage("Date of Visit")),
        roTimeOfVisit: Yup.string().nullable(),
        rmName: Yup.string().nullable(),
        conductedByAgency: Yup.string().nullable(),
        companyRepMet: Yup.string().nullable(),
        addressOfPlaceMet: Yup.string()
          .nullable()
          .max(250, ({ max }) => `Maximum ${max} characters allowed`)
          .matches(SPECIAL_CHARACTERS_NEWLINE_REGEX, getInvalidMessage("Address of place met")),
        counterfeitProducts: Yup.string().nullable(),
        signatoryMetDuringVisit: Yup.string().nullable(),
        reasonForNotmeetingSignatory: Yup.string()
          .nullable()
          .when("signatoryMetDuringVisit", {
            is: signatoryMetDuringVisit => signatoryMetDuringVisit === "No",
            then: Yup.string() // NOSONAR
              .required(getRequiredMessage("Reason for not meeting"))
              .max(MAX_ADDRESS_FIELD_LENGTH, ({ max }) => `Maximum ${max} characters allowed`)
              .matches(
                SPECIAL_CHARACTERS_NEWLINE_REGEX,
                getInvalidMessage("Reason for not meeting")
              )
          }),
        eidValidationDone: Yup.string()
          .nullable()
          .when("signatoryMetDuringVisit", {
            is: signatoryMetDuringVisit => signatoryMetDuringVisit === "Yes",
            then: Yup.string().required(getRequiredMessage("EID validation")) // NOSONAR
          }),
        signatoryRelationship: Yup.object({
          relationship: Yup.string().nullable(),
          others: Yup.string()
            .nullable()
            .when("relationship", {
              is: relationship => relationship === "others",
              then: Yup.string().required(getRequiredMessage("Others")) // NOSONAR
            })
        }),
        isChangeInCompnayProfile: Yup.string().nullable(),
        changeInCompnayProfileRemarks: Yup.string()
          .nullable()
          .when("isChangeInCompnayProfile", {
            is: isChangeInCompnayProfile => isChangeInCompnayProfile === "Yes",
            then: Yup.string().required(getRequiredMessage("Change in company profile remarks")) // NOSONAR
          }),
        isprofileChangeUpdatedInSystem: Yup.string().nullable(),
        estimatedComapnyAsset: Yup.string().nullable(),
        assetrdescription: Yup.string()
          .nullable()
          .max(1000, ({ max }) => `Max ${max} characters allowed`)
          .matches(
            SPECIAL_CHARACTERS_NEWLINE_REGEX,
            getInvalidMessage("Description/ breakdown of asset and value")
          ),
        isRTAVehicleListProvided: Yup.string().nullable(),
        isCompanyInIslamicProhibitedActivity: Yup.string().nullable(),
        rmAdditionalComments: Yup.string()
          .nullable()
          .max(1000, ({ max }) => `Max ${max} characters allowed`)
          .matches(SPECIAL_CHARACTERS_NEWLINE_REGEX, getInvalidMessage("RM additional comment")),
        soAdditionalComments: Yup.string()
          .nullable()
          .max(1000, ({ max }) => `Max ${max} characters allowed`)
          .matches(
            SPECIAL_CHARACTERS_NEWLINE_REGEX,
            getInvalidMessage("SM/Unit Head additional comment")
          ),
        hodAdditionalComments: Yup.string()
          .nullable()
          .max(1000, ({ max }) => `Max ${max} characters allowed`)
          .matches(SPECIAL_CHARACTERS_NEWLINE_REGEX, getInvalidMessage("HOD additional comment")),
        roAdditionalComments: Yup.string()
          .nullable()
          .max(1000, ({ max }) => `Max ${max} characters allowed`)
          .matches(SPECIAL_CHARACTERS_NEWLINE_REGEX, getInvalidMessage("RO additional comment"))
      })
    ),
    customerBackground: Yup.string()
      .nullable()
      .max(MAX_COMPANY_BACKGROUND_LENGTH, ({ max }) => `Max ${max} characters allowed`)
      .matches(SPECIAL_CHARACTERS_NEWLINE_REGEX, getInvalidMessage(COMPANY_BACKGROUND)),
    premisesDetails: Yup.object({
      premiseType: Yup.string().nullable(),
      premiseDesc: Yup.string()
        .nullable()
        .max(1000, ({ max }) => `Max ${max} characters allowed`)
        .matches(SPECIAL_CHARACTERS_NEWLINE_REGEX, getInvalidMessage("Description of Premises")),
      isSignboardSighted: Yup.string().nullable(),
      otherCompanySignboardSighted: Yup.string().nullable(),
      relationshipDetails: Yup.string()
        .nullable()
        .max(1000, ({ max }) => `Max ${max} characters allowed`)
        .matches(SPECIAL_CHARACTERS_NEWLINE_REGEX, getInvalidMessage("Relationship")),
      makaniNumber: Yup.string()
        .nullable()
        .max(Max_MAKANI_NUMBER_LENGTH, ({ max }) => `Maximum ${max} characters allowed`)
        .matches(NUMBER_REGEX, getInvalidMessage("Ejari Number")),
      longitude: Yup.number().nullable(),
      latitude: Yup.number().nullable(),
      countryCode: Yup.string().nullable(),
      telephoneNo: Yup.string().nullable(),
      detailsOfAddressProof: Yup.array().nullable()
    }),
    additionalDetails: Yup.object({
      tenancyContractProvided: Yup.string().nullable(),
      tenantNameOnContract: Yup.string()
        .nullable()
        .max(80, ({ max }) => `Maximum ${max} characters allowed`)
        .matches(SPECIAL_CHARACTERS_REGEX, getInvalidMessage("Name")),

      stockSighted: Yup.string().nullable(),
      estimatedStockValueAED: Yup.string()
        .nullable()
        .max(13, ({ max }) => `Maximum ${max} characters allowed`)
        .matches(NUMBER_REGEX, getInvalidMessage("Estimated value of stock")),
      companySharingPremises: Yup.string().nullable(),
      remarksSharingPremises: Yup.string()
        .nullable()
        .when("companySharingPremises", {
          is: companySharingPremises => companySharingPremises === "Yes",
          then: Yup.string().required(getRequiredMessage("Remarks")) // NOSONAR
        }),
      stockDiffDescription: Yup.string()
        .nullable()
        .max(255, "Max length is 255"),
      stockValueConfirmed: Yup.string().nullable(),
      stockValueConfirmedRemarks: Yup.string()
        .nullable()
        .matches(
          SPECIAL_CHARACTERS_NEWLINE_REGEX,
          getInvalidMessage("Stock value confirmed remarks")
        ),
      approxNumberofStaff: Yup.string()
        .nullable()
        .max(10, "Max length is 10")
        .matches(NUMBER_REGEX, getInvalidMessage("Approximate number of staff"))
    })
  });

export const initialVisitValidations = {
  contactSalutation: "",
  contactfullName: "",
  countryCode: UAE_CODE,
  mobileNo: "",
  contactDesignation: "",
  roDateOfVisit: "",
  roTimeOfVisit: "",
  roName: "",
  rmName: "",
  conductedByAgency: "",
  rmTimeOfVisit: "",
  rmDateOfVisit: "",
  rmCountryCode: "",
  rmMobileNo: "",
  addressOfPlaceMet: "",
  counterfeitProducts: "No",
  signatoryMetDuringVisit: "",
  reasonForNotmeetingSignatory: "",
  signatoryRelationship: {
    relationship: "",
    others: ""
  },
  eidValidationDone: "",
  isChangeInCompnayProfile: "",
  changeInCompnayProfileRemarks: "",
  isprofileChangeUpdatedInSystem: "",
  estimatedComapnyAsset: "",
  assetrdescription: "",
  isRTAVehicleListProvided: "",
  isCompanyInIslamicProhibitedActivity: "",
  roAdditionalComments: "",
  rmAdditionalComments: "",
  soAdditionalComments: "",
  hodAdditionalComments: ""
};

export const initialValues = {
  visitValidations: [initialVisitValidations]
};

export const changeDateProspectHandler = (_, value, path) =>
  isValid(value) && { [path]: format(value, DATE_FORMAT) };

export const changeTimeProspectHandler = (_, value, path) =>
  isValid(value) && { [path]: format(new Date(value), DATE_TIME_FORMAT) };

// Reporter component for customerBackground field invalid state transitions
export const CustomerBackgroundInvalidReporter = ({ value, onFieldInvalidChange }) => {
  const invalid = !!value && value.length > MAX_COMPANY_BACKGROUND_LENGTH;
  const lastRef = React.useRef(invalid);
  React.useEffect(() => {
    if (lastRef.current !== invalid) {
      lastRef.current = invalid;
      onFieldInvalidChange("customerBackground", invalid);
    }
  }, [invalid, onFieldInvalidChange]);
  return null;
};

CustomerBackgroundInvalidReporter.propTypes = {
  value: PropTypes.string,
  onFieldInvalidChange: PropTypes.func.isRequired
};
